import { useMemo } from 'react'
import { Timer } from 'lucide-react'

import { useRoadbook } from '../../data/roadbookContext'
import {
  EICHUNG_MIN_ETAPPEN,
  EICHUNG_MIN_STUNDEN,
  fahrzeitbilanz,
  MIN_ABDECKUNG,
  prozent,
} from '../../lib/fahrzeit'
import { anzahl, duration, hours } from '../../lib/format'

/**
 * Was die Aufzeichnungen über die geschätzten Fahrzeiten sagen - und was daraus
 * für die Etappen folgt, die noch kommen.
 *
 * ## Warum hier und nicht im Tagebuch
 *
 * Weil hier die Planung steht. Im Tagebuch liegt die einzelne Aufzeichnung, und
 * dort steht auch, was sie über ihren Tag sagt (features/diary/TrackPanel.tsx).
 * Diese Zahl hier ist eine andere: sie handelt nicht von einem gefahrenen Tag,
 * sondern von den nächsten - und die Frage, ob der Tagesplan trägt, stellt sich
 * jemand, der auf die Etappenliste sieht, nicht jemand, der einen Abend
 * nachträgt.
 *
 * Dieselbe Überlegung wie beim Streckenblock darüber, und derselbe Platz: dort
 * stehen Planung und berechnete Strecke nebeneinander, hier geplante und
 * gefahrene Zeit. Zusammen sind es die beiden Achsen einer Etappe.
 *
 * ## Was der Block nicht tut
 *
 * Er schreibt nichts. Kein Knopf übernimmt den gemessenen Zuschlag ins
 * Fahrzeug, keine Etappenstunde wird nachgezogen. Die ausführliche Begründung
 * steht über lib/fahrzeit.ts; die kurze ist, dass eine Korrektur an diesem
 * Zuschlag keine einzige geplante Etappenstunde repariert und die Grundlage für
 * eine stille Änderung ohnehin zu schmal ist. Was hilft, ist die Hochrechnung -
 * und die steht da, ohne irgendetwas anzufassen.
 */
export function FahrzeitenPanel() {
  const { trip, diary } = useRoadbook()
  const bilanz = useMemo(() => fahrzeitbilanz(trip, diary), [trip, diary])

  // Ohne eine einzige Aufzeichnung gibt es hier nichts zu sagen. Ein Block, der
  // erklärt, was er zeigen würde, wenn es etwas zu zeigen gäbe, steht auf einer
  // frisch geplanten Reise sechzehn Etappen lang im Weg - und das Tagebuch sagt
  // an der Stelle, an der eine Datei hingehört, ohnehin schon, was aus ihr wird.
  if (bilanz.mitAufzeichnung === 0) return null

  return (
    <section className="strecken kein-druck">
      <h4 className="block-titel">
        <Timer size={13} /> Fahrzeiten
      </h4>

      {bilanz.etappen === 0 ? (
        <p className="strecken-text">
          {anzahl(bilanz.mitAufzeichnung, 'Aufzeichnung liegt', 'Aufzeichnungen liegen')} vor, aber
          keine davon lässt sich mit der Planung vergleichen. Dafür braucht es auf derselben Etappe
          eine aktuelle berechnete Strecke und eine Aufzeichnung, die mindestens{' '}
          {prozent(MIN_ABDECKUNG)} des Tages enthält — an Kilometern und an Zeit. Ein Tag, an dem
          zwischen zwei Dateien Stunden fehlen, sagt über die Fahrzeit zu wenig.
        </p>
      ) : (
        <p className="strecken-text">
          Über <b>{anzahl(bilanz.etappen, 'Etappe', 'Etappen')}</b> mit Aufzeichnung und aktueller
          Strecke: <b>{hours(bilanz.fahrtH)} h</b> tatsächlich am Steuer gegenüber{' '}
          {hours(bilanz.dienstH)} h laut Routendienst.
          {bilanz.unvergleichbar > 0 &&
            ` Nicht mitgezählt sind ${anzahl(bilanz.unvergleichbar, 'Aufzeichnung', 'Aufzeichnungen')} mit zu geringer Abdeckung in Kilometern oder Zeit oder ohne aktuelle Strecke.`}
        </p>
      )}

      {bilanz.etappen > 0 && !bilanz.belastbar && (
        <p className="strecken-text">
          Für eine Hochrechnung ist das zu wenig. Sie beginnt bei{' '}
          {anzahl(EICHUNG_MIN_ETAPPEN, 'Etappe', 'Etappen')} und {String(EICHUNG_MIN_STUNDEN)}{' '}
          gemessenen Fahrstunden — aus einer einzelnen Fahrt folgt nichts über ein Fahrzeug, das
          sechzehn Tage lang beladen über Pässe soll.
        </p>
      )}

      {bilanz.belastbar && (
        <p className="strecken-zahlen">
          Faktor auf die Fahrzeit des Dienstes: <b>{bilanz.faktor.toFixed(2)}</b> — gemessen
          entspricht das einem Zuschlag von <b>{prozent(bilanz.zuschlagGemessen)}</b>, am Fahrzeug
          eingetragen sind {prozent(bilanz.zuschlagGeplant)} · <b>{hours(bilanz.pausenH)} h</b>{' '}
          Pause unterwegs, das sind {duration(bilanz.pauseJeFahrstunde * 3600)} je Fahrstunde
        </p>
      )}

      {bilanz.rest && (
        <p className="strecken-text">
          Vor dir liegen noch <b>{anzahl(bilanz.rest.etappen, 'Etappe', 'Etappen')}</b> mit
          berechneter Strecke, für die {hours(bilanz.rest.geplantH)} h im Plan stehen. Nach dem
          bisher Gefahrenen werden daraus <b>{hours(bilanz.rest.fahrtH)} h</b> am Steuer, dazu{' '}
          {hours(bilanz.rest.pausenH)} h Pause — zusammen{' '}
          <b>{hours(bilanz.rest.fahrtH + bilanz.rest.pausenH)} h</b>. Die Pause steht in keiner
          geplanten Stunde drin.
        </p>
      )}

      <p className="abschluss">
        Geändert wird davon nichts. Der Zuschlag steht am Fahrzeug im Reiter Daten, weil ihn dort
        jemand eingetippt hat, und die Stunden der Etappen sind Handwerte — beide bleiben, wie sie
        sind. Ein nachgezogener Zuschlag würde ohnehin keine geplante Etappenstunde ändern, sondern
        nur, was künftigen Strecken vorgeschlagen wird.
      </p>
    </section>
  )
}
